import { Api, ApiConfig } from "./ApiClient";

interface SecurityData {
  token: string;
}

// Global reference to auth context for token refresh
let authContextRef: any = null;

export const setAuthContextRef = (authContext: any) => {
  authContextRef = authContext;
};

class ApiService {
  private apiClient: Api<SecurityData>;

  constructor() {
    const config: ApiConfig<SecurityData> = {
      baseUrl: this.getBaseUrl(),
      securityWorker: (securityData) => {
        if (securityData?.token) {
          return {
            headers: {
              Authorization: `Bearer ${securityData.token}`,
            },
          };
        }
        return {};
      },
    };

    this.apiClient = new Api(config);
  }

  public getBaseUrl(): string {
    const hostname = window.location.hostname;
    if (hostname.includes(".sharepark.net")) {
      return `https://${hostname.replace("app", "api")}`;
    }
    return "https://localhost:5001";
  }

  public setAuthToken(token: string | null) {
    if (token) {
      this.apiClient.setSecurityData({ token });
    } else {
      this.apiClient.setSecurityData(null);
    }
  }

  // Wrapper method to handle 401 errors and retry with refreshed token
  private async executeWithRetry<T>(apiCall: () => Promise<T>): Promise<T> {
    try {
      return await apiCall();
    } catch (error: any) {
      // Check if it's a 401 error
      if (error?.status === 401 && authContextRef?.checkValidToken) {
        console.log("Got 401 error, attempting token refresh...");

        try {
          // Refresh token
          await authContextRef.checkValidToken();

          // Retry the original request
          console.log("Token refreshed, retrying request...");
          return await apiCall();
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          throw error; // Throw original error
        }
      }

      throw error; // Re-throw non-401 errors
    }
  }

  // Profile methods
  public getProfile() {
    return this.executeWithRetry(() => this.apiClient.api.v1ProfileList());
  }

  public updateProfile(
    data: Parameters<typeof this.apiClient.api.v1ProfileUpdate>[0]
  ) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1ProfileUpdate(data)
    );
  }

  public reissueQrCode() {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1QrCodeReissueCreate()
    );
  }

  public deleteAccount() {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1ProfileDelete()
    );
  }

  // Reservation methods
  public getReservations() {
    return this.executeWithRetry(() => this.apiClient.api.v1ReservationsList());
  }

  public createReservation(
    data: Parameters<typeof this.apiClient.api.v1ReservationsCreate>[0]
  ) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1ReservationsCreate(data)
    );
  }

  public calculateReservation(
    data: Parameters<typeof this.apiClient.api.v1ReservationsCalculateCreate>[0]
  ) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1ReservationsCalculateCreate(data)
    );
  }

  public cancelReservation(id: string) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1ReservationsCancelUpdate(id)
    );
  }

  // Site methods
  public getSites() {
    return this.executeWithRetry(() => this.apiClient.api.v1SitesList());
  }

  // Vehicle methods
  public getVehicles() {
    return this.executeWithRetry(() => this.apiClient.api.v1VehiclesList());
  }

  public createVehicle(
    data: Parameters<typeof this.apiClient.api.v1VehiclesCreate>[0]
  ) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1VehiclesCreate(data)
    );
  }

  public updateVehicle(
    id: string,
    data: Parameters<typeof this.apiClient.api.v1VehiclesUpdate>[1]
  ) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1VehiclesUpdate(id, data)
    );
  }

  public deleteVehicle(id: string) {
    return this.executeWithRetry(() => this.apiClient.api.v1VehiclesDelete(id));
  }

  // Transaction methods
  public getTransactions(
    query?: Parameters<typeof this.apiClient.api.v1TransactionsList>[0]
  ) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1TransactionsList(query)
    );
  }

  // get config
  public getConfig() {
    return this.executeWithRetry(() => this.apiClient.api.v1ConfigList());
  }

  // Invoice methods
  public getInvoice(id: string) {
    return this.executeWithRetry(() => this.apiClient.api.v1InvoicesDetail(id));
  }

  // Visitor methods
  public getVisitor(id: string) {
    return this.executeWithRetry(() => this.apiClient.api.v1VisitorDetail(id));
  }

  // Payment Methods
  public getPaymentMethods() {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1StripePaymentMethodsList()
    );
  }

  public setDefaultPaymentMethod(paymentMethodId: string) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1StripePaymentMethodsDefaultCreate(paymentMethodId)
    );
  }

  public removePaymentMethod(paymentMethodId: string) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1StripePaymentMethodsDetachDelete(paymentMethodId)
    );
  }

  public createSetupIntent(apiVersion?: string) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1SetupIntentsCreate({ apiVersion })
    );
  }

  public createCustomerPortal(returnUrl?: string) {
    return this.executeWithRetry(() =>
      this.apiClient.api.v1StripeCustomerPortalCreate({ returnUrl })
    );
  }
}

// Create a singleton instance
export const apiService = new ApiService();
